import React, { useEffect, useState, useRef } from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { supabase, PHOTO_UPLOAD_URL, VIDEO_UPLOAD_URL } from "../config";

const Form = ({ selectedSlot, onSuccess, onClose, editMode = false, initialData = null }) => {
  const [formData, setFormData] = useState({
    storyteller_telugu: "",
    storyteller_english: "",
    writer_story_title: "",
    introduction: "",
    whatsapp: "",
    email: "",
    story_length_mins: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const teluguInputRef = useRef(null);
  const photoInputRef = useRef(null);

  // Video upload state
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState("");
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [videoValidationError, setVideoValidationError] = useState("");
  const videoInputRef = useRef(null);

  // Pre-populate form data in edit mode
  useEffect(() => {
    if (editMode && initialData) {
      setFormData({
        storyteller_telugu: initialData.storyteller_telugu || "",
        storyteller_english: initialData.storyteller_english || "",
        writer_story_title: initialData.writer_story_title || "",
        introduction: initialData.introduction || "",
        whatsapp: initialData.whatsapp || "",
        email: initialData.email || "",
        story_length_mins: initialData.story_length_mins != null ? String(initialData.story_length_mins) : "",
      });
      if (initialData.photo_url) {
        setPhotoPreviewUrl(initialData.photo_url);
      }
      if (initialData.video_url) {
        setVideoPreviewUrl(initialData.video_url);
      }
    }
  }, [editMode, initialData]);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://www.google.com/jsapi";
    script.async = true;

    script.onload = () => {
      if (window.google) {
        window.google.load("elements", "1", {
          packages: "transliteration",
          callback: () => {
            const control =
              new window.google.elements.transliteration.TransliterationControl({
                sourceLanguage: "en",
                destinationLanguage: ["te"], // Telugu
                transliterationEnabled: true,
              });

            if (teluguInputRef.current?.id) {
              control.makeTransliteratable([teluguInputRef.current.id]);
            }
          },
        });
      }
    };

    document.body.appendChild(script);
  }, []);

  const validateField = (name, value) => {
    let newErrors = { ...errors };

    if (name === "storyteller_telugu") {
      const teluguRegex = /^[\u0C00-\u0C7F\s]+$/; // Telugu Unicode Range
      if (!teluguRegex.test(value)) {
        newErrors.storyteller_telugu = "Only Telugu characters are allowed.";
      } else {
        delete newErrors.storyteller_telugu;
      }
    }

    if (name === "storyteller_english" && value.length > 50) {
      newErrors.storyteller_english = "Maximum 50 characters allowed.";
    } else if (name === "storyteller_english") {
      delete newErrors.storyteller_english;
    }

    if (name === "writer_story_title" && value.length > 100) {
      newErrors.writer_story_title = "Maximum 100 characters allowed.";
    } else if (name === "writer_story_title") {
      delete newErrors.writer_story_title;
    }

    if (name === "introduction" && value.length > 250) {
      newErrors.introduction = "Maximum 250 characters allowed.";
    } else if (name === "introduction") {
      delete newErrors.introduction;
    }

    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        newErrors.email = "Enter a valid email address.";
      } else {
        delete newErrors.email;
      }
    }

    setErrors(newErrors);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
    setFormData({ ...formData, [name]: value });
  };

  const handlePhoneChange = (value) => {
    setFormData({ ...formData, whatsapp: value });
    if (!value) {
      setErrors((prev) => ({ ...prev, whatsapp: "Enter a valid phone number." }));
    } else {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy.whatsapp;
        return copy;
      });
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      alert("Please select a JPEG or PNG image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("Photo must be smaller than 5MB.");
      return;
    }

    setPhotoFile(file);
    setPhotoPreviewUrl(URL.createObjectURL(file));
  };

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVideoValidationError("");

    // Validate file type
    if (!["video/mp4", "video/quicktime", "video/webm"].includes(file.type)) {
      setVideoValidationError("Please select an MP4, MOV, or WebM video.");
      return;
    }

    // Validate file size (30MB)
    if (file.size > 30 * 1024 * 1024) {
      setVideoValidationError("Video must be smaller than 30MB.");
      return;
    }

    // Validate duration and orientation using HTMLVideoElement
    const videoEl = document.createElement("video");
    videoEl.preload = "metadata";
    const objectUrl = URL.createObjectURL(file);
    videoEl.src = objectUrl;

    videoEl.onloadedmetadata = () => {
      if (videoEl.duration > 150) {
        setVideoValidationError("Video must be shorter than 2 minutes 30 seconds.");
        URL.revokeObjectURL(objectUrl);
        return;
      }

      if (videoEl.videoWidth > videoEl.videoHeight) {
        setVideoValidationError(
          "Please upload a vertical (portrait) video. The video height must be greater than its width."
        );
        URL.revokeObjectURL(objectUrl);
        return;
      }

      // All validations passed
      setVideoFile(file);
      setVideoPreviewUrl(objectUrl);
    };

    videoEl.onerror = () => {
      setVideoValidationError("Could not read video file. Please try a different file.");
      URL.revokeObjectURL(objectUrl);
    };
  };

  const uploadPhotoToGDrive = (file, slotId, firstName) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = reader.result.split(",")[1];
          const safeName = (firstName || "unknown").replace(/[^a-zA-Z0-9]/g, "");
          const ext = file.type === "image/png" ? "png" : "jpg";
          const res = await fetch(PHOTO_UPLOAD_URL, {
            method: "POST",
            body: JSON.stringify({
              image: base64,
              mimeType: file.type,
              fileName: `${slotId}_${safeName}_photo_${Date.now()}.${ext}`,
            }),
          });
          const result = await res.json();
          if (result.success) {
            resolve(result.thumbnailUrl);
          } else {
            reject(new Error(result.error || "Upload failed"));
          }
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  };

  const uploadVideoToGDrive = (file, slotId, firstName) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = reader.result.split(",")[1];
          const safeName = (firstName || "unknown").replace(/[^a-zA-Z0-9]/g, "");
          const extMap = { "video/mp4": "mp4", "video/quicktime": "mov", "video/webm": "webm" };
          const ext = extMap[file.type] || "mp4";
          const res = await fetch(VIDEO_UPLOAD_URL, {
            method: "POST",
            body: JSON.stringify({
              image: base64,
              mimeType: file.type,
              fileName: `${slotId}_${safeName}_video_${Date.now()}.${ext}`,
              subfolder: "Videos",
            }),
          });
          const result = await res.json();
          if (result.success) {
            resolve(result.fileUrl || result.thumbnailUrl);
          } else {
            reject(new Error(result.error || "Video upload failed"));
          }
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read video file"));
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedSlot) {
      alert("Please select a time slot before submitting.");
      return;
    }

    const hasErrors = Object.keys(errors).length > 0;
    if (hasErrors) {
      alert("Please correct the errors before submitting.");
      return;
    }

    setIsSubmitting(true);

    try {
      // Upload photo if a new file was selected
      let photoUrl = editMode ? (initialData?.photo_url || null) : null;
      if (photoFile) {
        try {
          setIsUploading(true);
          const firstName = formData.storyteller_english.trim().split(/\s+/)[0];
          photoUrl = await uploadPhotoToGDrive(photoFile, selectedSlot, firstName);
        } catch (uploadErr) {
          console.error("Photo upload failed:", uploadErr);
          const continueWithout = window.confirm(
            "Photo upload failed. Continue without photo?"
          );
          if (!continueWithout) {
            setIsSubmitting(false);
            setIsUploading(false);
            return;
          }
          photoUrl = editMode ? (initialData?.photo_url || null) : null;
        } finally {
          setIsUploading(false);
        }
      }

      // Upload video if a new file was selected
      let videoUrl = editMode ? (initialData?.video_url || null) : null;
      if (videoFile) {
        try {
          setIsUploadingVideo(true);
          const firstName = formData.storyteller_english.trim().split(/\s+/)[0];
          videoUrl = await uploadVideoToGDrive(videoFile, selectedSlot, firstName);
        } catch (uploadErr) {
          console.error("Video upload failed:", uploadErr);
          const continueWithout = window.confirm(
            "Video upload failed. Continue without video?"
          );
          if (!continueWithout) {
            setIsSubmitting(false);
            setIsUploadingVideo(false);
            return;
          }
          videoUrl = editMode ? (initialData?.video_url || null) : null;
        } finally {
          setIsUploadingVideo(false);
        }
      }

      const storyLengthMins = formData.story_length_mins
        ? parseFloat(formData.story_length_mins)
        : null;

      if (editMode) {
        // EDIT MODE: Update existing storyteller record only
        const { error: updateError } = await supabase
          .from("storytellers")
          .update({
            storyteller_telugu: formData.storyteller_telugu,
            storyteller_english: formData.storyteller_english,
            writer_story_title: formData.writer_story_title,
            introduction: formData.introduction,
            whatsapp: formData.whatsapp,
            email: formData.email,
            photo_url: photoUrl,
            video_url: videoUrl,
            story_length_mins: storyLengthMins,
          })
          .eq("slot_id", selectedSlot);

        if (updateError) throw updateError;
        alert("Registration updated successfully!");
      } else {
        // REGISTER MODE: Book the slot + insert storyteller
        // Step 1: Update the `is_booked` field in `slots` table
        const { error: slotError } = await supabase
          .from("slots")
          .update({ is_booked: true })
          .eq("slot_id", selectedSlot);

        if (slotError) throw slotError;

        // Step 2: Insert storyteller's data into `storytellers` table
        const { error: storytellerError } = await supabase
          .from("storytellers")
          .insert([
            {
              storyteller_telugu: formData.storyteller_telugu,
              storyteller_english: formData.storyteller_english,
              writer_story_title: formData.writer_story_title,
              introduction: formData.introduction,
              whatsapp: formData.whatsapp,
              email: formData.email,
              photo_url: photoUrl,
              video_url: videoUrl,
              story_length_mins: storyLengthMins,
              slot_id: selectedSlot,
            },
          ]);

        if (storytellerError) throw storytellerError;
        alert("Registration successful!");
      }

      setFormData({
        storyteller_telugu: "",
        storyteller_english: "",
        writer_story_title: "",
        introduction: "",
        whatsapp: "",
        email: "",
        story_length_mins: "",
      });
      setPhotoFile(null);
      setPhotoPreviewUrl("");
      setVideoFile(null);
      setVideoPreviewUrl("");
      setVideoValidationError("");

      // notify parent (Register.js) to close modal + refresh slots
      if (typeof onSuccess === "function") {
        await onSuccess();
      }
    } catch (error) {
      console.error("Error submitting form:", error?.message || error);
      alert("Error submitting form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full bg-white p-6 rounded-lg shadow-xl border border-gray-300"
    >
      {/* Storyteller Name (Telugu) */}
      <label className="block mb-4">
        <span className="text-gray-800 font-medium">
          కథకుల పేరు - తెలుగులో (Storyteller Name in Telugu)
        </span>
        <input
          type="text"
          id="storyteller_telugu"
          name="storyteller_telugu"
          ref={teluguInputRef}
          value={formData.storyteller_telugu}
          onChange={handleChange}
          className="w-full mt-2 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          required
        />
        {errors.storyteller_telugu && (
          <p className="text-red-600 text-sm">{errors.storyteller_telugu}</p>
        )}
      </label>

      {/* Storyteller Name (English) */}
      <label className="block mb-4">
        <span className="text-gray-800 font-medium">Storyteller Name (In English)</span>
        <input
          type="text"
          name="storyteller_english"
          value={formData.storyteller_english}
          onChange={handleChange}
          className="w-full mt-2 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter storyteller name"
          required
        />
        {errors.storyteller_english && (
          <p className="text-red-600 text-sm">{errors.storyteller_english}</p>
        )}
      </label>

      {/* Writer's Name + Story Title */}
      <label className="block mb-4">
        <span className="text-gray-800 font-medium">Writer's Name + Story Title</span>
        <input
          type="text"
          name="writer_story_title"
          value={formData.writer_story_title}
          onChange={handleChange}
          className="w-full mt-2 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter writer's name + story title"
          required
        />
        {errors.writer_story_title && (
          <p className="text-red-600 text-sm">{errors.writer_story_title}</p>
        )}
      </label>

      {/* Introduction */}
      <label className="block mb-4">
        <span className="text-gray-800 font-medium">
          Storyteller's 5 Line Summarized Introduction
        </span>
        <textarea
          name="introduction"
          value={formData.introduction}
          onChange={handleChange}
          className="w-full mt-2 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none h-24"
          placeholder="Enter a short introduction"
          required
        />
        {errors.introduction && <p className="text-red-600 text-sm">{errors.introduction}</p>}
      </label>

      {/* Story Length in Minutes (Optional) */}
      <label className="block mb-4">
        <span className="text-gray-800 font-medium">Story Length in Minutes (Optional)</span>
        <input
          type="number"
          name="story_length_mins"
          value={formData.story_length_mins}
          onChange={handleChange}
          step="0.5"
          min="0"
          max="15"
          className="w-full mt-2 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="e.g. 8"
        />
      </label>

      {/* WhatsApp Number */}
      <label className="block mb-4">
        <span className="text-gray-800 font-medium">WhatsApp Number</span>
        <PhoneInput
          international
          value={formData.whatsapp}
          onChange={handlePhoneChange}
          className="w-full mt-2 p-2 border rounded-md"
        />
        {errors.whatsapp && <p className="text-red-600 text-sm">{errors.whatsapp}</p>}
      </label>

      {/* Email Address */}
      <label className="block mb-6">
        <span className="text-gray-800 font-medium">Email Address</span>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className="w-full mt-2 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter email address"
          required
        />
        {errors.email && <p className="text-red-600 text-sm">{errors.email}</p>}
      </label>

      {/* Photo Upload (Optional) */}
      <div className="block mb-6">
        <span className="text-gray-800 font-medium">Photo (Optional)</span>
        <p className="text-sm text-gray-500 mt-1 mb-2">
          Please upload a passport-like photo covering just head and shoulders. JPEG or PNG, max 5MB.
        </p>

        {photoPreviewUrl && (
          <div className="mb-2">
            <img
              src={photoPreviewUrl}
              alt="Photo preview"
              className="w-24 h-24 object-cover rounded-md border border-gray-300"
            />
          </div>
        )}

        <input
          type="file"
          accept="image/jpeg,image/png"
          onChange={handlePhotoChange}
          ref={photoInputRef}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => photoInputRef.current?.click()}
          className="px-4 py-2 border rounded-md text-sm text-gray-700 hover:bg-gray-50"
        >
          {photoPreviewUrl ? "Change Photo" : "Choose Photo"}
        </button>

        {isUploading && (
          <p className="text-sm text-blue-600 mt-1">Uploading photo...</p>
        )}
      </div>

      {/* Video Upload (Optional) */}
      <div className="block mb-6">
        <span className="text-gray-800 font-medium">Promotional Video (Optional)</span>
        <p className="text-sm text-gray-500 mt-1 mb-2">
          Upload a short vertical (portrait) video, under 2 min 30 sec. MP4, MOV, or WebM, max 30MB.
        </p>

        {videoPreviewUrl && (
          <div className="mb-2">
            <video
              src={videoPreviewUrl}
              controls
              className="w-48 rounded-md border border-gray-300"
              style={{ maxHeight: "200px" }}
            />
          </div>
        )}

        {videoValidationError && (
          <p className="text-red-600 text-sm mb-2">{videoValidationError}</p>
        )}

        <input
          type="file"
          accept="video/mp4,video/quicktime,video/webm"
          onChange={handleVideoChange}
          ref={videoInputRef}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => videoInputRef.current?.click()}
          className="px-4 py-2 border rounded-md text-sm text-gray-700 hover:bg-gray-50"
        >
          {videoPreviewUrl ? "Change Video" : "Choose Video"}
        </button>

        {isUploadingVideo && (
          <p className="text-sm text-blue-600 mt-1">Uploading video... This may take a moment.</p>
        )}
      </div>

      {/* Buttons */}
      <div className="flex gap-3">
        <button
          type="submit"
          className="flex-1 bg-blue-600 text-white py-3 rounded-md hover:bg-blue-700 transition-all disabled:opacity-50"
          disabled={isSubmitting || isUploading || isUploadingVideo}
        >
          {isUploadingVideo
            ? "Uploading video..."
            : isUploading
            ? "Uploading photo..."
            : isSubmitting
            ? (editMode ? "Saving..." : "Submitting...")
            : (editMode ? "Save" : "Submit")}
        </button>

        {typeof onClose === "function" && (
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-3 rounded-md border bg-white hover:bg-gray-50 text-gray-700"
            disabled={isSubmitting}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

export default Form;
